import { useState, useMemo } from 'react';
import { Checkbox, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import type { IColumnVisibilityState } from '../hooks/useColumnVisibility';
import { resolveColumnKey } from '../utils/resolveColumnKey';
import type { ColumnType } from 'antd/es/table';

interface IProps<T> {
  columns: ColumnType<T>[];
  visibilityState: IColumnVisibilityState;
  onVisibilityChange: (key: string, visible: boolean) => void;
}

const getTitle = (col: ColumnType<any>, key: string): string => {
  if (typeof col.title === 'string') return col.title;
  return String(col.title ?? key);
};

const ColumnConfigDropdown = <T extends object>({
  columns,
  visibilityState,
  onVisibilityChange,
}: IProps<T>) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredColumns = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return columns;

    return columns.filter(col => {
      const key = resolveColumnKey(col);
      if (!key) return false;

      const title = getTitle(col, key).toLowerCase();
      return title.includes(term);
    });
  }, [columns, searchTerm]);

  return (
    <DropdownContainer>
      <SearchWrapper>
        <Input
          placeholder="Search columns..."
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          allowClear
          size="small"
        />
      </SearchWrapper>

      <ColumnList>
        {filteredColumns.map(col => {
          const key = resolveColumnKey(col);
          if (!key) return null;

          const title = getTitle(col, key);
          const isVisible = visibilityState[key] !== false;

          return (
            <ColumnItem key={key}>
              <Checkbox
                checked={isVisible}
                onChange={e => onVisibilityChange(key, e.target.checked)}
              >
                {title}
              </Checkbox>
            </ColumnItem>
          );
        })}

        {filteredColumns.length === 0 && (
          <NoResults>No columns found</NoResults>
        )}
      </ColumnList>
    </DropdownContainer>
  );
};

const DropdownContainer = styled.div`
  min-width: 180px;
`;

const SearchWrapper = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ColumnList = styled.div`
  max-height: 250px;
  overflow-y: auto;
`;

const ColumnItem = styled.div`
  padding: 8px 12px;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colors.cardHover};
  }
`;

const NoResults = styled.div`
  padding: 12px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export default ColumnConfigDropdown;
